import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import EditableImage from "../../landingEditor/EditableImage";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { VisualColorControl } from "../inspector/VisualSettingControls";

function isPrimitiveCollection(property) {
  return Boolean(property?.item?.type) && !Array.isArray(property?.item?.fields);
}

function createCollectionItem(property) {
  if (isPrimitiveCollection(property)) return "";
  const next = {};
  (property?.item?.fields || []).forEach((field) => {
    if (!field?.key) return;
    if (field.key === "id") next[field.key] = `item-${Date.now().toString(36)}`;
    else if (/position/i.test(field.key)) next[field.key] = field.defaultValue || "center";
    else if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) next[field.key] = field.defaultValue ?? true;
    else next[field.key] = field.defaultValue ?? "";
  });
  return next;
}

function CollectionStyleFields({ fields = [], form, disabled, onCommit }) {
  if (!fields.length) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia de la tarjeta</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {fields.map((field) => {
            const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
            if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
              const fallback = String(field.defaultValue || "transparent");
              return (
                <VisualColorControl
                  key={field.path}
                  label={field.label}
                  value={String(value ?? fallback)}
                  fallback={fallback}
                  disabled={disabled}
                  allowExactCss
                  helperText="Valor explícito de las tarjetas de esta sección."
                  onChange={(next) => onCommit?.(field.path, String(next || "").trim() || fallback)}
                  onReset={() => onCommit?.(field.path, fallback)}
                />
              );
            }
            if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
              return <TextField key={field.path} fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
            }
            return <TextField key={field.path} fullWidth size="small" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ maxLength: field.maxLength || 300 }} helperText={/Shadow/i.test(field.path || "") ? "CSS exacto de sombra." : undefined} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function CollectionField({ property, index, field, form, baselineForm, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const path = `${property.path}.${index}.${field.key}`;
  const value = getValueAtDescriptorPath(form, path, "");
  if (field.key === "id") return null;
  if (field.type === SEGMENT_FIELD_TYPES.IMAGE) {
    return (
      <EditableImage
        value={value}
        label={field.label || "Imagen"}
        disabled={disabled}
        onUpload={(file, metadata) => onUploadImage?.(path, file, metadata)}
        onRemove={() => onRemoveImage?.(path)}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label || field.key}</InputLabel>
        <Select label={field.label || field.key} value={value ?? ""} onChange={(event) => onCommit?.(path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  return (
    <LandingCopyTextField
      fullWidth
      size="small"
      label={field.label || field.key}
      field={field}
      path={path}
      form={form}
      baselineForm={baselineForm}
      value={value ?? ""}
      disabled={disabled}
      multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
      minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
      commitMode="change"
      onCommit={(nextValue) => onCommit?.(path, nextValue)}
    />
  );
}

export default function CollectionItemEditor({ descriptor, form, baselineForm, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const collection = descriptor?.collection;
  const property = descriptor?.property || collection?.property;
  if (!property) return null;
  const itemsValue = getValueAtDescriptorPath(form, property.path, []);
  const items = Array.isArray(itemsValue) ? itemsValue : [];
  const maxItems = Number.isFinite(property?.limits?.maxItems) ? property.limits.maxItems : 40;
  const manager = descriptor?.kind === "collection-manager";
  const selectedIndex = Number.isInteger(collection?.index) ? collection.index : 0;
  const primitive = isPrimitiveCollection(property);
  const styleFields = Array.isArray(descriptor?.styleFields) ? descriptor.styleFields : [];

  const updateItems = (next) => onCommit?.(property.path, next.slice(0, maxItems));
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    updateItems(next);
  };
  const remove = (index) => updateItems(items.filter((_, itemIndex) => itemIndex !== index));
  const add = () => {
    if (items.length >= maxItems) return;
    updateItems([...items, createCollectionItem(property)]);
  };

  if (!manager) {
    const fields = property.item?.fields || [];
    return (
      <Stack spacing={1.2}>
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
          Editás este elemento dentro de {property.label || "la colección"}.
        </Typography>
        {primitive ? (
          <LandingCopyTextField
            fullWidth
            size="small"
            label={property.label || "Texto"}
            field={property.item}
            path={`${property.path}.${selectedIndex}`}
            form={form}
            baselineForm={baselineForm}
            value={items[selectedIndex] ?? ""}
            disabled={disabled}
            commitMode="change"
            onCommit={(nextValue) => {
              const next = [...items];
              next[selectedIndex] = nextValue;
              updateItems(next);
            }}
          />
        ) : fields.map((field) => (
          <CollectionField
            key={`${property.path}:${selectedIndex}:${field.key}`}
            property={property}
            index={selectedIndex}
            field={field}
            form={form}
            baselineForm={baselineForm}
            disabled={disabled}
            onCommit={onCommit}
            onUploadImage={onUploadImage}
            onRemoveImage={onRemoveImage}
          />
        ))}
        <CollectionStyleFields fields={styleFields} form={form} disabled={disabled} onCommit={onCommit} />
        <Stack direction="row" spacing={0.5}>
          <IconButton aria-label="Mover arriba" disabled={disabled || selectedIndex <= 0} onClick={() => move(selectedIndex, -1)}><KeyboardArrowUpRoundedIcon /></IconButton>
          <IconButton aria-label="Mover abajo" disabled={disabled || selectedIndex >= items.length - 1} onClick={() => move(selectedIndex, 1)}><KeyboardArrowDownRoundedIcon /></IconButton>
          <Button color="error" disabled={disabled} startIcon={<DeleteOutlineRoundedIcon />} onClick={() => remove(selectedIndex)} sx={{ textTransform: "none" }}>Quitar</Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 950 }}>{property.label || descriptor.label || "Elementos"}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>{items.length} / {maxItems}</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} disabled={disabled || items.length >= maxItems} onClick={add} sx={{ textTransform: "none", borderRadius: 999 }}>Agregar</Button>
      </Stack>

      <CollectionStyleFields fields={styleFields} form={form} disabled={disabled} onCommit={onCommit} />

      {!items.length ? <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>Todavía no hay elementos.</Typography> : null}

      <Stack spacing={1}>
        {items.map((item, index) => (
          <Box key={item?.id || `${property.path}:${index}`} sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>Elemento {index + 1}</Typography>
              <Stack direction="row" spacing={0.2}>
                <IconButton size="small" aria-label={`Mover elemento ${index + 1} arriba`} disabled={disabled || index === 0} onClick={() => move(index, -1)}><KeyboardArrowUpRoundedIcon fontSize="small" /></IconButton>
                <IconButton size="small" aria-label={`Mover elemento ${index + 1} abajo`} disabled={disabled || index === items.length - 1} onClick={() => move(index, 1)}><KeyboardArrowDownRoundedIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" aria-label={`Quitar elemento ${index + 1}`} disabled={disabled} onClick={() => remove(index)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
            {primitive ? (
              <LandingCopyTextField
                fullWidth
                size="small"
                label={property.label || "Texto"}
                field={property.item}
                path={`${property.path}.${index}`}
                form={form}
                baselineForm={baselineForm}
                value={String(item ?? "")}
                disabled={disabled}
                commitMode="change"
                onCommit={(nextValue) => {
                  const next = [...items];
                  next[index] = nextValue;
                  updateItems(next);
                }}
              />
            ) : (
              <Stack spacing={1}>
                {(property.item?.fields || []).map((field) => (
                  <CollectionField
                    key={`${property.path}:${index}:${field.key}`}
                    property={property}
                    index={index}
                    field={field}
                    form={form}
                    baselineForm={baselineForm}
                    disabled={disabled}
                    onCommit={onCommit}
                    onUploadImage={onUploadImage}
                    onRemoveImage={onRemoveImage}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
